import { useState } from 'react'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Plus, Wheat } from 'lucide-react'
import { getV1MeRetrieveQueryOptions, useV1SchoolsList, useV1SchoolTypesList } from '@/api/client'
import { V1SchoolsListState, type V1SchoolsListVoivodeship } from '@/api/model'
import type { SchoolList, StateEnum } from '@/api/model'
import { OpenProgramToggle } from '@/components/open-program-toggle'
import { PageHeader } from '@/components/page-header'
import { StatusBadge, type StatusTone } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DataTable, type Column, type StateOption } from '@/components/data-table'
import { SchoolForm } from '@/components/schools/school-form'
import { SchoolProposalsLink } from '@/components/schools/school-proposals-link'
import { queryHasHttpError } from '@/lib/query-state'
import { voivodeshipOptions } from '@/lib/voivodeships'

// Radix Select can't hold an empty-string value, so "no filter" uses this sentinel
// and is mapped back to `undefined` (the param is simply omitted) before the query.
const ALL = '__all__'

export const Route = createFileRoute('/_panel/szkoly/')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    if (me.status === 200 && me.data.role === 'teacher') {
      throw redirect({ to: '/' })
    }
  },
  component: SchoolsPage,
})

const PAGE_SIZE = 25

const stateOptions: StateOption<V1SchoolsListState>[] = [
  { value: V1SchoolsListState.active, label: 'Aktywne' },
  { value: V1SchoolsListState.archived, label: 'Zarchiwizowane' },
  { value: V1SchoolsListState.all, label: 'Wszystkie' },
]

const stateTone: Record<StateEnum, StatusTone> = {
  active: 'green',
  archived: 'zinc',
  deleted: 'red',
}

/**
 * FR-007: two distinct empty states. When a search or a non-default status filter
 * is active, "no schools match these criteria" (clearable); when the default view
 * (active, no search) is empty, the system genuinely has no schools yet.
 */
export function schoolsEmptyMessage(search: string, state: V1SchoolsListState): string {
  const filtering = search.trim() !== '' || state !== V1SchoolsListState.active
  return filtering
    ? 'Brak szkół spełniających kryteria. Wyczyść wyszukiwanie i filtry, aby zobaczyć wszystkie.'
    : 'W systemie nie ma jeszcze żadnej szkoły.'
}

function SchoolsPage() {
  const navigate = useNavigate()
  const [creating, setCreating] = useState(false)
  const [page, setPage] = useState(1)
  const [showOpenProgram, setShowOpenProgram] = useState(false)
  const [search, setSearch] = useState('')
  const [state, setState] = useState<V1SchoolsListState>(V1SchoolsListState.active)
  const [voivodeship, setVoivodeship] = useState<string>(ALL)
  const [schoolType, setSchoolType] = useState<string>(ALL)
  const [city, setCity] = useState('')
  // Powiązanie z RSPO jako filtr, nie pill — slot pilla zajmuje już `state`.
  const [rspoLink, setRspoLink] = useState<string>(ALL)
  const [ordering, setOrdering] = useState('name')

  const typesQuery = useV1SchoolTypesList({ page_size: 100 })
  const schoolTypes = typesQuery.data?.status === 200 ? typesQuery.data.data.results : []

  const query = useV1SchoolsList({
    state,
    is_open_program: showOpenProgram ? undefined : false,
    search: search || undefined,
    voivodeship: voivodeship === ALL ? undefined : (voivodeship as V1SchoolsListVoivodeship),
    school_type: schoolType === ALL ? undefined : Number(schoolType),
    city: city || undefined,
    has_rspo: rspoLink === ALL ? undefined : rspoLink === 'linked',
    ordering: ordering || undefined,
    page,
    page_size: PAGE_SIZE,
  })
  const data = query.data?.status === 200 ? query.data.data : undefined

  // Any filter reset returns to page 1 so the pager can't point past the new result set.
  const resetPage = () => setPage(1)

  if (creating) {
    return (
      <>
        <PageHeader
          title={
            <button
              type="button"
              onClick={() => setCreating(false)}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="size-4" /> Szkoły
            </button>
          }
        />
        <div className="p-6">
          <h2 className="mb-6 text-lg font-semibold">Nowa szkoła</h2>
          <SchoolForm onCancel={() => setCreating(false)} />
        </div>
      </>
    )
  }

  const columns: Column<SchoolList>[] = [
    {
      header: 'Nazwa',
      sortKey: 'name',
      cell: (s) => (
        <div className="flex flex-col">
          <span className="flex items-center gap-2">
            <span className="font-medium">{s.name}</span>
            {s.rspo_is_zsckr ? (
              <StatusBadge tone="green">
                <Wheat className="size-3" />
                ZSCKR
              </StatusBadge>
            ) : null}
          </span>
          {s.short_name ? (
            <span className="text-xs text-muted-foreground">{s.short_name}</span>
          ) : null}
        </div>
      ),
    },
    {
      header: 'Typ',
      cell: (s) => <span className="text-muted-foreground">{s.school_type_name}</span>,
    },
    {
      header: 'Województwo',
      sortKey: 'voivodeship',
      cell: (s) => <span className="text-muted-foreground">{s.voivodeship_label}</span>,
    },
    {
      header: 'Miejscowość',
      sortKey: 'city',
      cell: (s) => <span className="text-muted-foreground">{s.city}</span>,
    },
    {
      header: 'Dołączenie',
      sortKey: 'joined_program_on',
      cell: (s) => <span className="text-muted-foreground">{s.joined_program_on}</span>,
    },
    {
      header: 'Kontakt główny',
      cell: (s) => (
        <span className="text-muted-foreground">
          {s.primary_contact
            ? `${s.primary_contact.full_name} · ${s.primary_contact.position}`
            : '—'}
        </span>
      ),
    },
    {
      // Class counters have no DB column (null until the TR-600 Class model) — not
      // sortable (T017): no `sortKey`, so the backend never gets `ordering=…_count`.
      header: 'Klasy',
      className: 'text-right',
      cell: (s) => (
        <span className="tabular-nums text-muted-foreground">{s.active_class_count ?? '—'}</span>
      ),
    },
    {
      header: 'Stan',
      sortKey: 'state',
      cell: (s) => <StatusBadge tone={stateTone[s.state]}>{s.state_label}</StatusBadge>,
    },
  ]

  const filters = (
    <>
      <OpenProgramToggle
        checked={showOpenProgram}
        onCheckedChange={(value) => {
          setShowOpenProgram(value)
          resetPage()
        }}
      />
      <Select
        value={voivodeship}
        onValueChange={(v) => {
          setVoivodeship(v)
          resetPage()
        }}
      >
        <SelectTrigger className="h-8 w-44">
          <SelectValue placeholder="Województwo" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>Wszystkie województwa</SelectItem>
          {voivodeshipOptions.map(([value, label]) => (
            <SelectItem key={value} value={value}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={schoolType}
        onValueChange={(v) => {
          setSchoolType(v)
          resetPage()
        }}
      >
        <SelectTrigger className="h-8 w-40">
          <SelectValue placeholder="Typ szkoły" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>Wszystkie typy</SelectItem>
          {schoolTypes.map((t) => (
            <SelectItem key={t.id} value={String(t.id)}>
              {t.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        value={city}
        onChange={(e) => {
          setCity(e.target.value)
          resetPage()
        }}
        placeholder="Miejscowość"
        className="h-8 w-36"
      />
      <Select
        value={rspoLink}
        onValueChange={(v) => {
          setRspoLink(v)
          resetPage()
        }}
      >
        <SelectTrigger className="h-8 w-40">
          <SelectValue placeholder="Powiązanie RSPO" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>RSPO: wszystkie</SelectItem>
          <SelectItem value="linked">Powiązane</SelectItem>
          <SelectItem value="unlinked">Niepowiązane</SelectItem>
        </SelectContent>
      </Select>
    </>
  )

  return (
    <>
      <PageHeader
        title="Szkoły"
        actions={
          <div className="flex items-center gap-2">
            <SchoolProposalsLink />
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus /> Załóż szkołę
            </Button>
          </div>
        }
      />
      <div className="p-6">
        <DataTable
          columns={columns}
          rows={data?.results ?? []}
          rowKey={(s) => s.id}
          count={data?.count ?? 0}
          page={page}
          onPageChange={setPage}
          pageSize={PAGE_SIZE}
          search={search}
          onSearchChange={(v) => {
            setSearch(v)
            setPage(1)
          }}
          searchPlaceholder="Szukaj szkoły…"
          stateValue={state}
          stateOptions={stateOptions}
          onStateChange={(v) => {
            setState(v)
            setPage(1)
          }}
          filters={filters}
          ordering={ordering}
          onOrderingChange={(o) => {
            setOrdering(o)
            setPage(1)
          }}
          isLoading={query.isLoading}
          isError={queryHasHttpError(query)}
          onRetry={() => void query.refetch()}
          emptyMessage={schoolsEmptyMessage(search, state)}
          onRowClick={(s) =>
            navigate({ to: '/szkoly/$szkolaId', params: { szkolaId: String(s.id) } })
          }
          countNoun={['szkoła', 'szkół']}
        />
      </div>
    </>
  )
}
